import React, { useState } from 'react';
import {
    Modal,
    Form,
    Input,
    Button,
    message,
    Typography,
    Alert,
    Space
} from 'antd';
import {
    LockOutlined,
    EyeInvisibleOutlined,
    EyeTwoTone,
    SecurityScanOutlined
} from '@ant-design/icons';
import {change_password_first_login} from "../../Services/user.service";
import {setFirstLogin} from "../../Actions/user.action";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";

const { Title, Text } = Typography;
const { Password } = Input;

const PasswordChangeModal = ({ visible, onSuccess }) => {
    const { t } = useTranslation("users");
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [passwordStrength, setPasswordStrength] = useState(0);
    const dispatch = useDispatch();

    const calculatePasswordStrength = (password) => {
        let strength = 0;
        if (password.length >= 8) strength += 1;
        if (/[A-Z]/.test(password)) strength += 1;
        if (/[0-9]/.test(password)) strength += 1;
        if (/[^A-Za-z0-9]/.test(password)) strength += 1;
        return strength;
    };

    const getPasswordStrengthColor = (strength) => {
        const colors = ['#ff4d4f', '#ff7875', '#ffc53d', '#73d13d', '#52c41a'];
        return colors[Math.min(strength, colors.length - 1)];
    };

    const handlePasswordChange = (value) => {
        const strength = calculatePasswordStrength(value);
        setPasswordStrength(strength);
    };

    const handleSubmit = async (values) => {
        if (values.newPassword !== values.confirmPassword) {
            message.error(t("password.errors.mismatch"));
            return;
        }

        setLoading(true);
        try {
            await change_password_first_login(values.newPassword, values.confirmPassword);
            message.success(t("password.success"));
            dispatch(setFirstLogin(false));
            form.resetFields();
            setPasswordStrength(0);
        } catch (error) {
            message.error(error.response?.data?.message || t("password.errors.change"));
        } finally {
            setLoading(false);
        }
    };

    const passwordRules = [
        { required: true, message: t("password.rules.required") },
        { min: 8, message: t("password.rules.min") },
        { pattern: /[A-Z]/, message: t("password.rules.uppercase") },
        { pattern: /[0-9]/, message: t("password.rules.number") },
        { pattern: /[^A-Za-z0-9]/, message: t("password.rules.special") }
    ];

    return (
        <Modal
            title={
                <Space>
                    <SecurityScanOutlined style={{ color: '#1890ff' }} />
                    <span>{t("password.modalTitle")}</span>
                </Space>
            }
            open={visible}
            onCancel={() => {}}
            footer={null}
            closable={false}
            maskClosable={false}
            keyboard={false}
            width={500}
            centered
        >
            <Alert
                message={t("password.firstLoginTitle")}
                description={t("password.firstLoginDesc")}
                type="warning"
                showIcon
                style={{ marginBottom: 24 }}
            />

            <Form form={form} layout="vertical" onFinish={handleSubmit} autoComplete="off">
                <Form.Item
                    label={t("password.new")}
                    name="newPassword"
                    rules={passwordRules}
                    extra={
                        <div style={{ marginTop: 8 }}>
                            <Text type="secondary">{t("password.security")}: </Text>
                            <Text strong style={{ color: getPasswordStrengthColor(passwordStrength) }}>
                                {t(`password.strength.${passwordStrength}`)}
                            </Text>
                            <div
                                style={{
                                    height: 4,
                                    background: '#f0f0f0',
                                    marginTop: 4,
                                    borderRadius: 2
                                }}
                            >
                                <div
                                    style={{
                                        height: '100%',
                                        width: `${(passwordStrength / 4) * 100}%`,
                                        background: getPasswordStrengthColor(passwordStrength),
                                        borderRadius: 2,
                                        transition: 'all 0.3s'
                                    }}
                                />
                            </div>
                        </div>
                    }
                >
                    <Password
                        placeholder={t("password.newPlaceholder")}
                        prefix={<LockOutlined />}
                        iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
                        onChange={(e) => handlePasswordChange(e.target.value)}
                        size="large"
                    />
                </Form.Item>

                <Form.Item
                    label={t("password.confirm")}
                    name="confirmPassword"
                    rules={[
                        { required: true, message: t("password.rules.confirm") },
                        ({ getFieldValue }) => ({
                            validator(_, value) {
                                if (!value || getFieldValue('newPassword') === value) {
                                    return Promise.resolve();
                                }
                                return Promise.reject(new Error(t("password.errors.mismatch")));
                            },
                        }),
                    ]}
                >
                    <Password
                        placeholder={t("password.confirmPlaceholder")}
                        prefix={<LockOutlined />}
                        iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
                        size="large"
                    />
                </Form.Item>

                <Form.Item>
                    <Button
                        type="primary"
                        htmlType="submit"
                        loading={loading}
                        size="large"
                        block
                        style={{
                            background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
                            border: 'none',
                            fontWeight: 600
                        }}
                    >
                        {t("password.changeBtn")}
                    </Button>
                </Form.Item>
            </Form>

            <div style={{ textAlign: 'center', marginTop: 16 }}>
                <Text type="secondary" style={{ fontSize: '12px' }}>
                    {t("password.securityHint")}
                </Text>
            </div>
        </Modal>
    );
};

export default PasswordChangeModal;
